import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  Activity, 
  BookOpen, 
  CheckCircle, 
  PenLine,
  Archive,
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  AlertCircle,
  Shield,
  HelpCircle,
  Zap,
  RefreshCw
} from "lucide-react";

// Simple bar chart component
const SimpleBarChart = ({ data, label, color = "#7a9e7a", maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="space-y-2">
      {data.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <div className="w-20 text-xs text-foreground/60 truncate">{item.label}</div>
          <div className="flex-1 h-6 bg-[#e0ebe0] rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(item.value / max) * 100}%`,
                backgroundColor: color
              }}
            />
          </div>
          <div className="w-12 text-sm text-foreground font-medium text-right">{item.value}</div>
        </div>
      ))}
    </div>
  );
};

// Simple line chart for trends
const SimpleTrendChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.total), 1);
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1 || 1)) * 100,
    y: 100 - (d.total / maxValue) * 80,
    total: d.total,
    date: d._id
  }));
  
  const pathD = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ');
  
  return (
    <div className="relative" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {/* Grid lines */}
        <line x1="0" y1="20" x2="100" y2="20" stroke="#e0ebe0" strokeWidth="0.5" />
        <line x1="0" y1="50" x2="100" y2="50" stroke="#e0ebe0" strokeWidth="0.5" />
        <line x1="0" y1="80" x2="100" y2="80" stroke="#e0ebe0" strokeWidth="0.5" />
        
        {/* Area under line */}
        <path
          d={`${pathD} L 100 100 L 0 100 Z`}
          fill="url(#areaGradient)"
          opacity="0.3"
        />
        
        {/* Line */}
        <path
          d={pathD}
          fill="none"
          stroke="#7a9e7a"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        
        {/* Points */}
        {points.map((p) => (
          <circle
            key={`${p.x}-${p.y}`}
            cx={p.x}
            cy={p.y}
            r="1.5"
            fill="#5a8a5a"
          />
        ))}
        
        <defs>
          <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#7a9e7a" />
            <stop offset="100%" stopColor="#7a9e7a" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* X-axis labels */}
      {data.length > 0 && (
        <div className="flex justify-between text-xs text-foreground/40 mt-1">
          <span>{data[0]?._id?.slice(5)}</span>
          <span>{data[data.length - 1]?._id?.slice(5)}</span>
        </div>
      )}
    </div>
  );
};

const PatternIcon = ({ pattern }) => {
  const icons = {
    avoidance: Shield,
    uncertainty: HelpCircle,
    fear: AlertCircle,
    distraction: Zap
  };
  const Icon = icons[pattern] || Activity;
  return <Icon className="w-4 h-4" />;
};

export default function AdminAnalytics() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, engagementRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, {
          withCredentials: true
        }),
        axios.get(`${API}/admin/analytics/engagement?days=${period}`, {
          withCredentials: true
        })
      ]);
      setStats(statsRes.data);
      setEngagement(engagementRes.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="grid md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-28 bg-muted rounded-xl" />
              ))}
            </div>
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  const engagementRate = stats?.total_members > 0 
    ? Math.round((stats.active_members / stats.total_members) * 100) 
    : 0;

  // Prepare pattern data for chart
  const patternData = engagement?.patterns?.map(p => ({
    label: p._id.charAt(0).toUpperCase() + p._id.slice(1),
    value: p.count
  })) || [];

  // Pattern colors
  const patternColors = {
    avoidance: "#e57373",
    uncertainty: "#ffb74d",
    fear: "#ff8a65",
    distraction: "#64b5f6"
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Header */}
        <section className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="analytics-header">
          <div>
            <h1 className="text-3xl md:text-4xl text-foreground mb-2">Analytics</h1>
            <p className="text-muted-foreground">Platform performance and user engagement.</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px] bg-white border-[#c5d5c5]" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleRefresh}
              className="border-[#c5d5c5]"
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </section>

        {/* Overview Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {stats?.total_members || 0}
              </div>
              <div className="text-sm text-muted-foreground">Total Members</div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-500" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {engagement?.active_users || 0}
              </div>
              <div className="text-sm text-muted-foreground">Active ({period}d)</div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {engagement?.engagement_rate || 0}%
              </div>
              <div className="text-sm text-muted-foreground">Engagement Rate</div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-500" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {engagement?.weekly_resets || 0}
              </div>
              <div className="text-sm text-muted-foreground">Weekly Resets</div>
            </CardContent>
          </Card>
        </section>

        {/* Check-in Trend */}
        <section className="mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Daily Check-ins Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {engagement?.daily_checkins?.length > 0 ? (
                <SimpleTrendChart data={engagement.daily_checkins} height={160} />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No check-in data available for this period
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Two Column Layout */}
        <section className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Pattern Analysis */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-primary" />
                Why Users Say "No"
              </CardTitle>
            </CardHeader>
            <CardContent>
              {patternData.length > 0 ? (
                <div className="space-y-4">
                  {engagement?.patterns?.map((pattern, i) => (
                    <div key={pattern._id} className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${patternColors[pattern._id]}20` }}
                      >
                        <PatternIcon pattern={pattern._id} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-foreground font-medium capitalize">{pattern._id}</span>
                          <span className="text-sm text-foreground/60">{pattern.count}</span>
                        </div>
                        <div className="h-2 bg-[#e0ebe0] rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${(pattern.count / (engagement.patterns[0]?.count || 1)) * 100}%`,
                              backgroundColor: patternColors[pattern._id]
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No pattern data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Course Performance */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Course Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.course_stats?.length > 0 ? (
                <div className="space-y-4">
                  {stats.course_stats.map((course) => (
                    <div key={course.course_id}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-foreground font-medium text-sm">{course.title}</span>
                        <span className="text-sm text-primary font-medium">{course.completion_rate}%</span>
                      </div>
                      <div className="h-2 bg-[#e0ebe0] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#7a9e7a] to-[#5a8a5a] rounded-full"
                          style={{ width: `${course.completion_rate}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-foreground/40">
                        <span>{course.enrolled_count} enrolled</span>
                        <span>{course.completed_count} completed</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No course data available
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Additional Stats */}
        <section className="grid md:grid-cols-3 gap-6">
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#e8dcc8] flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#a89060]" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Lesson Completions</div>
                  <div className="text-2xl font-heading text-foreground">{stats?.lesson_completions || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#d8d0e8] flex items-center justify-center">
                  <PenLine className="w-5 h-5 text-[#7a6a9a]" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Journal Entries</div>
                  <div className="text-2xl font-heading text-foreground">{stats?.journal_entries || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#d0dce8] flex items-center justify-center">
                  <Archive className="w-5 h-5 text-[#5a7090]" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Top Vault Chamber</div>
                  <div className="text-lg font-heading text-foreground truncate">
                    {stats?.top_vault_chamber?._id?.replace("chamber_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "N/A"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}
